import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigationType} from 'react-router';
import type {Swiper as SwiperInstance} from 'swiper';

import NAVIGATION_TYPES from '@/consts/navigationTypes';
import useBadgeDetailRoute, {setBadgeDetailDepth} from '@/hooks/useBadgeDetailRoute';

const SLIDE_MS = 300;

export type StackEntry = {
    /** Stable Swiper slide key — never changes after creation. */
    key: number;
    badgeId: string;
    levelBadgeId: string | null;
};

/**
 *
 * PUSH  → appends a new entry + animates right after render
 * POP   → animates left + trims the stack after the animation
 * REPLACE (initial open) → resets to a single entry, no animation
 */
const useBadgeDetailStack = () => {
    const {badgeId, levelBadgeId, isOpen} = useBadgeDetailRoute();
    const navigationType = useNavigationType();
    const location = useLocation();

    const nextKeyRef = useRef(0);
    const swiperRef = useRef<SwiperInstance | null>(null);

    /**
     * Tracks how many actual history entries belong to this popup.
     * REPLACE+animateAsForward navigations grow the visual stack but NOT this counter,
     * so closePopup always pops the correct number of real history entries.
     */
    const historyDepthRef = useRef(0);

    // Stack kept in both a ref (readable inside timeouts) and state (drives rendering).
    const stackRef = useRef<StackEntry[]>([]);
    const [stack, setStackState] = useState<StackEntry[]>([]);
    const setStack = useCallback((next: StackEntry[]) => {
        stackRef.current = next;
        setStackState(next);
    }, []);

    // For PUSH: schedule a slideTo after the new slide has been rendered.
    const pendingSlideRef = useRef<{index: number; speed: number} | null>(null);

    // Initialized as closed so the first effect run always detects "just opened".
    const prevRef = useRef<{isOpen: boolean; badgeId: string | null; levelBadgeId: string | null}>({
        isOpen: false,
        badgeId: null,
        levelBadgeId: null,
    });

    // Sync depth tracker so closePopup always knows how far to pop.
    useEffect(() => {
        return () => setBadgeDetailDepth(0);
    }, []);

    // Main navigation effect.
    useEffect(() => {
        const prev = prevRef.current;
        prevRef.current = {isOpen, badgeId, levelBadgeId};

        // Popup closed → keep stack intact so the close animation shows content.
        // The Dialog Portal will unmount this component once the animation ends.
        if (!isOpen) return;
        if (!badgeId) return;

        // Popup just opened, or normal REPLACE (e.g. initial redirect) → single entry, no animation.
        if (
            !prev.isOpen ||
            (navigationType === NAVIGATION_TYPES.REPLACE &&
                !(location.state as {animateAsForward?: boolean} | null)?.animateAsForward)
        ) {
            setStack([{key: nextKeyRef.current++, badgeId, levelBadgeId}]);
            historyDepthRef.current = 1;
            setBadgeDetailDepth(1);
            return;
        }

        // No badge-related params changed (e.g. some other query param updated).
        if (badgeId === prev.badgeId && levelBadgeId === prev.levelBadgeId) return;

        // PUSH → animate forward, increment real history depth.
        if (navigationType === NAVIGATION_TYPES.PUSH) {
            const newStack = [...stackRef.current, {key: nextKeyRef.current++, badgeId, levelBadgeId}];
            pendingSlideRef.current = {index: newStack.length - 1, speed: SLIDE_MS};
            historyDepthRef.current += 1;
            setBadgeDetailDepth(historyDepthRef.current);
            setStack(newStack);
            return;
        }

        // REPLACE+animateAsForward → animate forward but no real history entry was pushed.
        // Slides before the new entry can't be returned to, so trim them after the animation.
        if (
            navigationType === NAVIGATION_TYPES.REPLACE &&
            (location.state as {animateAsForward?: boolean} | null)?.animateAsForward
        ) {
            const newEntry = {key: nextKeyRef.current++, badgeId, levelBadgeId};
            const newStack = [...stackRef.current, newEntry];
            pendingSlideRef.current = {index: newStack.length - 1, speed: SLIDE_MS};
            setStack(newStack);
            const timeoutId = window.setTimeout(() => {
                setStack([newEntry]);
                requestAnimationFrame(() => swiperRef.current?.slideTo(0, 0));
            }, SLIDE_MS + 100);
            return () => window.clearTimeout(timeoutId);
        }

        if (navigationType === NAVIGATION_TYPES.POP) {
            const targetIndex = Math.max(0, stackRef.current.length - 2);
            swiperRef.current?.slideTo(targetIndex, SLIDE_MS);
            const snapshot = stackRef.current;
            historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
            setBadgeDetailDepth(historyDepthRef.current);
            const timeoutId = window.setTimeout(() => setStack(snapshot.slice(0, -1)), SLIDE_MS + 50);
            return () => window.clearTimeout(timeoutId);
        }
    }, [badgeId, levelBadgeId, isOpen, navigationType, location.state, setStack]);

    // After PUSH renders the new slide, execute the pending slideTo.
    useEffect(() => {
        const pending = pendingSlideRef.current;
        if (!pending) return;
        pendingSlideRef.current = null;
        requestAnimationFrame(() => {
            swiperRef.current?.update();
            swiperRef.current?.slideTo(pending.index, pending.speed);
        });
    }, [stack]);

    const onSwiper = useCallback((s: SwiperInstance) => {
        swiperRef.current = s;
    }, []);

    return {stack, onSwiper};
};

export {SLIDE_MS};
export default useBadgeDetailStack;
